const BYTES_PER_MB = 1024 * 1024;
const DECIMAL_PLACES = 1;
const DEFAULT_MIN_ROWS = 1;
const HEADER_ROW_OFFSET = 2;
const MAX_DUPLICATE_DISPLAY = 5;

export type CsvValidationOptions = {
  maxRows?: number;
  minRows?: number;
  uniqueColumn?: string;
  maxFileSizeBytes?: number;
};

export type CsvValidationResult = {
  valid: boolean;
  error?: string;
  warnings?: string[];
};

export const validateCsvFile = (
  file: File,
  options: CsvValidationOptions = {},
): CsvValidationResult => {
  const { maxFileSizeBytes } = options;

  if (file.size === 0) {
    return {
      valid: false,
      error: 'CSV file is empty',
    };
  }

  if (maxFileSizeBytes && file.size > maxFileSizeBytes) {
    const maxSizeMB = (maxFileSizeBytes / BYTES_PER_MB).toFixed(DECIMAL_PLACES);
    const fileSizeMB = (file.size / BYTES_PER_MB).toFixed(DECIMAL_PLACES);
    return {
      valid: false,
      error: `File size (${fileSizeMB}MB) exceeds maximum allowed size of ${maxSizeMB}MB`,
    };
  }

  return { valid: true };
};

export const validateCsvRows = <T extends Record<string, any>>(
  rows: T[],
  options: CsvValidationOptions = {},
): CsvValidationResult => {
  const { maxRows, minRows = DEFAULT_MIN_ROWS, uniqueColumn } = options;
  const warnings: string[] = [];

  if (rows.length === 0) {
    return {
      valid: false,
      error: 'CSV file contains no data rows',
    };
  }

  if (rows.length < minRows) {
    return {
      valid: false,
      error: `CSV must contain at least ${minRows} row${minRows === 1 ? '' : 's'} (found ${rows.length})`,
    };
  }

  if (maxRows && rows.length > maxRows) {
    return {
      valid: false,
      error: `CSV contains too many rows. Maximum allowed: ${maxRows}, found: ${rows.length}`,
    };
  }

  if (uniqueColumn) {
    const seenValues = new Map<string, number[]>();

    rows.forEach((row, index) => {
      const value = row[uniqueColumn];
      if (value) {
        const normalizedValue = String(value).trim().toUpperCase();
        if (!seenValues.has(normalizedValue)) {
          seenValues.set(normalizedValue, []);
        }
        seenValues.get(normalizedValue)!.push(index + HEADER_ROW_OFFSET);
      }
    });

    const duplicates: string[] = [];
    seenValues.forEach((rowNumbers, value) => {
      if (rowNumbers.length > 1) {
        duplicates.push(`"${value}" appears in rows: ${rowNumbers.join(', ')}`);
      }
    });

    if (duplicates.length > 0) {
      return {
        valid: false,
        error: `Duplicate values found in column "${uniqueColumn}":\n${duplicates.slice(0, MAX_DUPLICATE_DISPLAY).join('\n')}${
          duplicates.length > MAX_DUPLICATE_DISPLAY
            ? `\n... and ${duplicates.length - MAX_DUPLICATE_DISPLAY} more`
            : ''
        }`,
      };
    }
  }

  return { valid: true, warnings: warnings.length > 0 ? warnings : undefined };
};

export const validateCsv = <T extends Record<string, any>>(
  file: File,
  rows: T[],
  options: CsvValidationOptions = {},
): CsvValidationResult => {
  const fileValidation = validateCsvFile(file, options);
  if (!fileValidation.valid) {
    return fileValidation;
  }

  return validateCsvRows(rows, options);
};
